"use client";

import { GameMetadataSection } from "@/components/feature/game-metadata-section";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { GameWithRelations } from "@/types/game";
import * as DialogPrimitive from "@radix-ui/react-dialog";

interface GameMetadataOverlayProps {
  game: GameWithRelations;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PANEL_GUTTER_X =
  "left-[max(0.75rem,env(safe-area-inset-left))] right-[max(0.75rem,env(safe-area-inset-right))]";

const PANEL_MAX_HEIGHT =
  "max-h-[calc(100dvh-max(1.5rem,env(safe-area-inset-top)+env(safe-area-inset-bottom)))]";

/**
 * Phone landscape only — desktop is also `landscape` and must keep hugging content.
 * Spell out the complete utilities so Tailwind's source scan can emit them.
 */
const PANEL_SHORT_LANDSCAPE_FILL = [
  "[@media(orientation:landscape)_and_(max-height:540px)]:top-[max(0.5rem,env(safe-area-inset-top))]",
  "[@media(orientation:landscape)_and_(max-height:540px)]:bottom-[max(0.5rem,env(safe-area-inset-bottom))]",
  "[@media(orientation:landscape)_and_(max-height:540px)]:my-0",
  "[@media(orientation:landscape)_and_(max-height:540px)]:h-auto",
].join(" ");

export function GameMetadataOverlay({ game, open, onOpenChange }: GameMetadataOverlayProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="game-metadata-overlay-backdrop"
          className={cn(
            "fixed inset-0 z-[60] bg-black/70 pointer-events-auto",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300 ease-out"
          )}
        />
        <DialogPrimitive.Content
          data-testid="game-metadata-overlay"
          className={cn(
            "fixed z-[60] mx-auto flex min-h-0 w-auto max-w-2xl flex-col overflow-hidden rounded-xl border border-white/10 bg-ink-light shadow-xl outline-none sm:rounded-2xl",
            PANEL_GUTTER_X,
            "bottom-[max(0.75rem,env(safe-area-inset-bottom))] top-auto",
            PANEL_MAX_HEIGHT,
            // Center without translate so the fade/slide-up motion isn't a corner fly-in.
            "sm:top-[max(0.75rem,env(safe-area-inset-top))] sm:bottom-[max(0.75rem,env(safe-area-inset-bottom))] sm:my-auto sm:h-fit",
            PANEL_SHORT_LANDSCAPE_FILL,
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:slide-out-to-bottom-8 data-[state=open]:slide-in-from-bottom-8",
            "duration-300 ease-out"
          )}
          aria-describedby={undefined}
        >
          <div
            className={cn(
              "flex min-w-0 shrink-0 items-center border-b border-white/10 px-4 py-3 sm:px-6 sm:py-4",
              "[@media(orientation:landscape)_and_(max-height:540px)]:py-2"
            )}
          >
            <DialogPrimitive.Title className="charlie-kingdom-font min-w-0 max-w-full text-balance break-words text-lg font-bold leading-tight text-white sm:text-xl">
              About {game.name}
            </DialogPrimitive.Title>
          </div>
          <div
            className={cn(
              "min-h-0 min-w-0 flex-auto overflow-x-auto overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-6",
              "[@media(orientation:landscape)_and_(max-height:540px)]:flex-1 [@media(orientation:landscape)_and_(max-height:540px)]:py-3"
            )}
            data-testid="game-metadata-overlay-content"
          >
            <GameMetadataSection game={game} />
          </div>
          <div
            data-testid="game-metadata-overlay-footer"
            className={cn(
              "sticky bottom-0 z-10 shrink-0 border-t border-white/10 bg-ink-light px-4 py-3 sm:px-6 sm:py-4",
              "[@media(orientation:landscape)_and_(max-height:540px)]:py-2"
            )}
          >
            <DialogPrimitive.Close asChild>
              <Button
                variant="brand"
                className={cn(
                  "h-12 w-full rounded-full px-6 text-base font-bold sm:h-14 sm:px-8 sm:text-lg",
                  "[@media(orientation:landscape)_and_(max-height:540px)]:h-11"
                )}
                aria-label="Close game info"
                data-testid="game-metadata-overlay-close"
              >
                Close
              </Button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
